import type { TrackBadge } from '../types';

/** A badge's 26px disc, and how far each further icon in it reaches (it overlaps by 6px). */
const BADGE_DISC_PX = 26;
const BADGE_EXTRA_ICON_PX = 20;

interface BadgeCluster {
    members: TrackBadge[];
    kinds: TrackBadge['kinds'];
}

/** Where a cluster is drawn: centred between its first and last member, its discs side by side. */
function clusterSpanPx(cluster: BadgeCluster, trackPx: number): [number, number] {
    const first = cluster.members[0].leftPct;
    const last = cluster.members[cluster.members.length - 1].leftPct;
    const centre = (((first + last) / 2) * trackPx) / 100;
    const half = (BADGE_DISC_PX + (cluster.kinds.length - 1) * BADGE_EXTRA_ICON_PX) / 2;
    return [centre - half, centre + half];
}

function joinClusters(a: BadgeCluster, b: BadgeCluster): BadgeCluster {
    const kinds = [...a.kinds];
    for (const kind of b.kinds) if (!kinds.includes(kind)) kinds.push(kind);
    return { members: [...a.members, ...b.members], kinds };
}

/**
 * Joins badges whose discs would overlap on a track `trackPx` wide into one badge between them
 * with every kind ("Delousing + Move" a week apart in Whole stocking), so no event hides under
 * another. Overlap is measured on what is drawn: a joined badge is wider and sits between its
 * members, so neighbours are compared with it, and joining repeats until no two overlap.
 * Badges kept apart are returned as they are.
 */
export function clusterBadges(badges: TrackBadge[], trackPx: number): TrackBadge[] {
    let clusters: BadgeCluster[] = [...badges]
        .sort((a, b) => a.leftPct - b.leftPct)
        .map((badge) => ({ members: [badge], kinds: [...badge.kinds] }));
    let joined = true;
    while (joined) {
        joined = false;
        for (let index = 0; index + 1 < clusters.length; index += 1) {
            const [, right] = clusterSpanPx(clusters[index], trackPx);
            const [left] = clusterSpanPx(clusters[index + 1], trackPx);
            if (left < right) {
                const merged = joinClusters(clusters[index], clusters[index + 1]);
                clusters = [...clusters.slice(0, index), merged, ...clusters.slice(index + 2)];
                joined = true;
                break;
            }
        }
    }
    return clusters.map(({ members, kinds }) =>
        members.length === 1
            ? members[0]
            : {
                  key: members.map((member) => member.key).join('|'),
                  leftPct: (members[0].leftPct + members[members.length - 1].leftPct) / 2,
                  kinds,
                  labelShown: true,
                  peers: [...new Set(members.flatMap((member) => member.peers))],
                  // A cluster across a chain row's handover opens the group of its first event.
                  groupId: members[0].groupId,
              }
    );
}
